import React, { useCallback, useEffect, useMemo, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import { formatCHF, pos } from "../../services/posService";
import {
	ErrorBox,
	MethodBadge,
	Modal,
	POSHeader,
	SaleStatusBadge,
	dateTimeOf,
	timeOf,
} from "./posShared";

const FILTERS = [
	["all", "Toutes"],
	["terminal", "Terminal"],
	["cash", "Espèces"],
];

export default function POSSalesHistory() {
	const [sessions, setSessions] = useState([]);
	const [sessionId, setSessionId] = useState("");
	const [sales, setSales] = useState([]);
	const [summary, setSummary] = useState(null);
	const [filter, setFilter] = useState("all");
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [selected, setSelected] = useState(null);

	useEffect(() => {
		pos
			.sessions()
			.then(({ sessions: list }) => {
				setSessions(list || []);
				if (list?.length) setSessionId(list[0].id);
				else setLoading(false);
			})
			.catch((e) => {
				setError(e.message);
				setLoading(false);
			});
	}, []);

	const load = useCallback(async () => {
		if (!sessionId) return;
		setLoading(true);
		setError("");
		try {
			const [{ sales: list }, { session }] = await Promise.all([
				pos.listSales(sessionId),
				pos.sessionSummary(sessionId),
			]);
			setSales(list || []);
			setSummary(session);
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, [sessionId]);

	useEffect(() => {
		load();
	}, [load]);

	const visible = useMemo(
		() => (filter === "all" ? sales : sales.filter((s) => s.payment_method === filter)),
		[sales, filter],
	);

	return (
		<div className="min-h-screen bg-[#F7F3ED] text-[#34251D]">
			<POSHeader title="Historique des ventes" icon={History} />
			<main className="mx-auto max-w-6xl space-y-5 px-4 py-8 sm:px-6">
				<div className="flex flex-wrap items-end gap-3">
					<label className="min-w-[260px] flex-1">
						<span className="mb-1 block text-sm font-medium">Journée de caisse</span>
						<select
							value={sessionId}
							onChange={(e) => setSessionId(e.target.value)}
							className="w-full rounded-xl border bg-white p-3"
						>
							{sessions.map((s) => (
								<option key={s.id} value={s.id}>
									{dateTimeOf(s.opened_at)} {s.status === "open" ? "— en cours" : `→ ${timeOf(s.closed_at)}`}
								</option>
							))}
						</select>
					</label>
					<button type="button" onClick={load} className="flex items-center gap-2 rounded-xl border bg-white px-4 py-3 text-sm hover:bg-[#F0E8E0]">
						<RefreshCw size={16} /> Actualiser
					</button>
				</div>

				{summary ? (
					<div className="grid gap-3 sm:grid-cols-3">
						<Total label="Total" value={summary.sales_total} count={summary.sales_count} strong />
						<Total label="Terminal" value={summary.terminal.total} count={summary.terminal.count} />
						<Total label="Espèces" value={summary.cash.total} count={summary.cash.count} />
					</div>
				) : null}

				<div className="flex gap-2">
					{FILTERS.map(([value, label]) => (
						<button
							key={value}
							type="button"
							onClick={() => setFilter(value)}
							aria-pressed={filter === value}
							className={`rounded-xl px-4 py-2 text-sm font-semibold ${filter === value ? "bg-[#34251D] text-white" : "bg-white shadow-sm"}`}
						>
							{label}
						</button>
					))}
				</div>

				<ErrorBox message={error} />

				<section className="overflow-hidden rounded-2xl border border-[#E6E1D6] bg-white">
					{loading ? <p className="p-5 text-sm">Chargement…</p> : null}
					{!loading && !sessions.length ? (
						<p className="p-5 text-sm text-gray-500">Aucune journée de caisse pour le moment.</p>
					) : null}
					{!loading && sessions.length > 0 && visible.length === 0 ? (
						<p className="p-5 text-sm text-gray-500">Aucune vente pour ce filtre.</p>
					) : null}
					{!loading && visible.length > 0 ? (
						<table className="w-full text-left text-sm">
							<thead className="bg-[#F7F3ED] text-xs uppercase tracking-wide text-gray-500">
								<tr>
									<th className="px-4 py-3">N°</th>
									<th className="px-4 py-3">Heure</th>
									<th className="hidden px-4 py-3 md:table-cell">Articles</th>
									<th className="px-4 py-3">Paiement</th>
									<th className="px-4 py-3">Statut</th>
									<th className="px-4 py-3 text-right">Montant</th>
								</tr>
							</thead>
							<tbody>
								{visible.map((sale) => (
									<tr
										key={sale.id}
										onClick={() => setSelected(sale)}
										className="cursor-pointer border-t border-[#F0EAE4] hover:bg-[#FBF8F4]"
									>
										<td className="px-4 py-3 font-semibold">{sale.sale_number}</td>
										<td className="px-4 py-3">{timeOf(sale.paid_at || sale.created_at)}</td>
										<td className="hidden max-w-[320px] truncate px-4 py-3 text-gray-600 md:table-cell">
											{sale.items.map((i) => `${i.quantity}× ${i.product_name}`).join(", ")}
										</td>
										<td className="px-4 py-3">
											<MethodBadge method={sale.payment_method} />
										</td>
										<td className="px-4 py-3">
											<SaleStatusBadge status={sale.status} />
										</td>
										<td className="px-4 py-3 text-right font-semibold">{formatCHF(sale.total_amount)}</td>
									</tr>
								))}
							</tbody>
						</table>
					) : null}
				</section>
			</main>

			{selected ? <SaleDetail sale={selected} onClose={() => setSelected(null)} /> : null}
		</div>
	);
}

function Total({ label, value, count, strong }) {
	return (
		<div className={`rounded-xl p-4 ${strong ? "bg-[#34251D] text-white" : "bg-white"}`}>
			<p className={`text-xs font-semibold uppercase tracking-wide ${strong ? "text-white/70" : "text-gray-500"}`}>{label}</p>
			<p className="text-2xl font-bold">{formatCHF(value)}</p>
			<p className={`text-xs ${strong ? "text-white/70" : "text-gray-500"}`}>
				{count} vente{count > 1 ? "s" : ""}
			</p>
		</div>
	);
}

function SaleDetail({ sale, onClose }) {
	const p = sale.payment || {};
	return (
		<Modal title={`Vente n° ${sale.sale_number}`} onClose={onClose}>
			<div className="space-y-4 text-sm">
				<div className="flex flex-wrap items-center gap-2">
					<MethodBadge method={sale.payment_method} />
					<SaleStatusBadge status={sale.status} />
					<span className="text-gray-500">{sale.service_type === "dine_in" ? "Sur place" : "À emporter"}</span>
					<span className="ml-auto text-gray-500">{dateTimeOf(sale.paid_at || sale.created_at)}</span>
				</div>
				<ul className="divide-y rounded-xl border">
					{sale.items.map((item, index) => (
						<li key={index} className="flex justify-between gap-3 p-3">
							<span>
								{item.quantity} × {item.product_name}
								{item.variant_name ? <span className="text-gray-500"> — {item.variant_name}</span> : null}
							</span>
							<span className="font-semibold">{formatCHF(item.line_total)}</span>
						</li>
					))}
				</ul>
				<div className="space-y-1">
					<Line label="Total" value={formatCHF(sale.total_amount)} strong />
					<Line label="dont TVA" value={formatCHF(sale.tax_amount)} />
					{sale.payment_method === "cash" && p.cash_received ? (
						<>
							<Line label="Reçu en espèces" value={formatCHF(p.cash_received)} />
							<Line label="Monnaie rendue" value={formatCHF(p.change)} />
						</>
					) : null}
					{sale.payment_method === "terminal" && p.card_last4 ? (
						<Line label="Carte" value={`${(p.card_brand || "").toUpperCase()} •••• ${p.card_last4}`} />
					) : null}
					{p.failure ? <Line label="Motif" value={p.failure} /> : null}
				</div>
			</div>
		</Modal>
	);
}

function Line({ label, value, strong }) {
	return (
		<div className={`flex justify-between ${strong ? "text-base font-bold" : "text-gray-600"}`}>
			<span>{label}</span>
			<span>{value}</span>
		</div>
	);
}
